/**
 * Pure bitmask helpers for TimedAction day fields.
 *
 * All operations use 2**n arithmetic and modulo/division to avoid bitwise
 * operators (airbnb no-bitwise rule). Semantics are identical to the
 * equivalent C/Java bitwise expressions.
 *
 * Bit 31 (last-day-of-month) is handled as the unsigned constant 2^31
 * (2147483648) to avoid JavaScript's signed-int-32 trap.
 */

/** Bit 31: last calendar day of the month (2^31 = 2147483648). */
export const LAST_DAY_BIT = 2 ** 31; // 2147483648

// ---------------------------------------------------------------------------
// Internal helpers — arithmetic bit tests, no bitwise ops
// ---------------------------------------------------------------------------

/** Test whether bit `n` (0-indexed) is set in `mask`. */
function isBitSet(mask: number, n: number): boolean {
  // Equivalent to (mask & (1 << n)) !== 0 without bitwise ops.
  // Use unsigned 32-bit modular arithmetic via Math.floor and remainder.
  const bit = 2 ** n;
  // Convert to unsigned 32-bit integer by adding 2^32 if negative.
  const unsigned = mask < 0 ? mask + 2 ** 32 : mask;
  return Math.floor(unsigned / bit) % 2 !== 0;
}

// ---------------------------------------------------------------------------
// days_of_week  (7-bit mask, bit 0 = Sunday … bit 6 = Saturday)
// ---------------------------------------------------------------------------

/**
 * Convert a days-of-week bitmask to an array of 7 booleans.
 * Index 0 = Sunday (bit 0), index 6 = Saturday (bit 6).
 */
export function daysOfWeekToBooleans(mask: number): boolean[] {
  return Array.from({ length: 7 }, (_, i) => isBitSet(mask, i));
}

/**
 * Convert an array of 7 booleans back to a days-of-week bitmask.
 * Index 0 = Sunday (bit 0), index 6 = Saturday (bit 6).
 */
export function booleansToDaysOfWeek(bools: boolean[]): number {
  return bools.slice(0, 7).reduce((acc, on, i) => (on ? acc + 2 ** i : acc), 0);
}

// ---------------------------------------------------------------------------
// days_of_month  (32-bit mask: bit i => day i+1; bit 31 => last day)
// ---------------------------------------------------------------------------

/**
 * Convert a days-of-month bitmask to an array of day numbers (1..31)
 * whose bit is set. Does NOT include the last-day sentinel (bit 31) —
 * use hasLastDay() for that.
 */
export function daysOfMonthToDays(mask: number): number[] {
  return Array.from({ length: 31 }, (_, i) => i + 1).filter((day) => isBitSet(mask, day - 1));
}

/**
 * Convert an array of day numbers (1..31) to a days-of-month bitmask.
 * Values outside 1..31 are ignored.
 */
export function daysToMonthMask(days: number[]): number {
  return days.filter((d) => d >= 1 && d <= 31).reduce((acc, day) => acc + 2 ** (day - 1), 0);
}

/**
 * Returns true when the "last day of month" sentinel (bit 31 = 2^31) is set.
 * Handles both positive (unsigned) and negative (signed 32-bit) representations.
 */
export function hasLastDay(mask: number): boolean {
  return isBitSet(mask, 31);
}

/**
 * Returns a new mask with the last-day sentinel bit set (on=true) or
 * cleared (on=false). Result is an unsigned 32-bit integer.
 */
export function withLastDay(mask: number, on: boolean): number {
  const unsigned = mask < 0 ? mask + 2 ** 32 : mask;
  const alreadySet = hasLastDay(mask);
  if (on && !alreadySet) {
    return unsigned + LAST_DAY_BIT;
  }
  if (!on && alreadySet) {
    return unsigned - LAST_DAY_BIT;
  }
  return unsigned;
}
